import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Wrench, Package, Clock, FileText, Cog } from 'lucide-react';
import { getORDemarrageToken, soumettreORDemarrage } from '../api';

/* ── Helpers ──────────────────────────────────────────────────────────── */
const fmtMoney = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
const fmtH     = (v) => v == null ? '—' : `${v} h`;

/* ── Composants UI ────────────────────────────────────────────────────── */
const Section = ({ icon: Icon, title, color = '#1A3A6E', children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      {Icon && <Icon size={15} style={{ color }} />}
      <p style={{ margin: 0, fontWeight: 700, fontSize: '0.8rem', color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        {title}
      </p>
    </div>
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <p style={{ margin: '0 0 2px', fontSize: '0.7rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
    <p style={{ margin: 0, fontSize: '0.875rem', color: '#1E293B', fontWeight: 500 }}>{value || '—'}</p>
  </div>
);

const Card = ({ children, style }) => (
  <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 20, marginBottom: 14, ...style }}>
    {children}
  </div>
);

const LignesTable = ({ lignes, colonnes }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
          {colonnes.map(c => (
            <th key={c.key} style={{ padding: '6px 8px', textAlign: c.align || 'left', color: '#64748B', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lignes.map((l, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #F1F5F9', background: i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
            {colonnes.map(c => (
              <td key={c.key} style={{ padding: '7px 8px', textAlign: c.align || 'left', color: c.bold ? '#1E293B' : '#374151', fontWeight: c.bold ? 700 : 400 }}>
                {c.render ? c.render(l) : (l[c.key] ?? '—')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SousTotalLine = ({ label, value, color = '#1A3A6E' }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, paddingTop: 8, marginTop: 4, borderTop: '1px dashed #E2E8F0' }}>
    <span style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 500 }}>{label}</span>
    <span style={{ fontSize: '0.9rem', fontWeight: 700, color }}>{fmtMoney(value)}</span>
  </div>
);

/* ── Composant principal ──────────────────────────────────────────────── */
export default function AtelierDemarrageSignaturePage() {
  const { token }           = useParams();
  const canvasRef           = useRef(null);
  const drawingRef          = useRef(false);
  const lastPosRef          = useRef({ x: 0, y: 0 });

  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [ordre,      setOrdre]      = useState(null);
  const [nom,        setNom]        = useState('');
  const [submitted,  setSubmitted]  = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getORDemarrageToken(token)
      .then(r => { setOrdre(r.data); setNom(r.data.signataire_nom || ''); })
      .catch(e => setError(e?.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  /* ── Canvas ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !ordre) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1A3A6E';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches?.[0] || e;
      return { x: (t.clientX - r.left) * (canvas.width / r.width), y: (t.clientY - r.top) * (canvas.height / r.height) };
    };
    const down = (e) => { e.preventDefault(); drawingRef.current = true;  lastPosRef.current = pos(e); };
    const move = (e) => {
      e.preventDefault();
      if (!drawingRef.current) return;
      const p = pos(e);
      ctx.beginPath(); ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(p.x, p.y); ctx.stroke(); lastPosRef.current = p;
    };
    const up = () => { drawingRef.current = false; };
    canvas.addEventListener('mousedown',  down);
    canvas.addEventListener('mousemove',  move);
    canvas.addEventListener('mouseup',    up);
    canvas.addEventListener('touchstart', down, { passive: false });
    canvas.addEventListener('touchmove',  move, { passive: false });
    canvas.addEventListener('touchend',   up);
    return () => {
      canvas.removeEventListener('mousedown',  down);
      canvas.removeEventListener('mousemove',  move);
      canvas.removeEventListener('mouseup',    up);
      canvas.removeEventListener('touchstart', down);
      canvas.removeEventListener('touchmove',  move);
      canvas.removeEventListener('touchend',   up);
    };
  }, [ordre]);

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
  };

  const isCanvasEmpty = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const blank = document.createElement('canvas');
    blank.width = c.width; blank.height = c.height;
    return c.toDataURL() === blank.toDataURL();
  };

  const handleSubmit = async () => {
    if (!nom.trim())     { alert('Merci de saisir votre nom complet.');  return; }
    if (isCanvasEmpty()) { alert("Merci d'apposer votre signature."); return; }
    setSubmitting(true);
    try {
      await soumettreORDemarrage(token, { signataire_nom: nom.trim(), signature_image_b64: canvasRef.current.toDataURL('image/png') });
      setSubmitted(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur lors de l'envoi de la signature");
    } finally { setSubmitting(false); }
  };

  /* ── États ─────────────────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC' }}>
      <Loader2 size={32} style={{ color: '#1A3A6E', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FEF2F2', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <p style={{ color: '#DC2626', fontWeight: 700, fontSize: '1rem', marginBottom: 8 }}>Lien invalide ou expiré</p>
        <p style={{ color: '#EF4444', fontSize: '0.875rem' }}>{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F0FDF4', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 380 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <CheckCircle2 size={32} style={{ color: '#16A34A' }} />
        </div>
        <p style={{ color: '#15803D', fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Signature enregistrée</p>
        <p style={{ color: '#166534', fontSize: '0.875rem' }}>
          {nom && <span>Merci <strong>{nom}</strong>. </span>}
          L'ordre de réparation <strong>{ordre?.numero}</strong> peut maintenant démarrer.
        </p>
      </div>
    </div>
  );

  /* ── Données ────────────────────────────────────────────────────────── */
  const pieces      = Array.isArray(ordre?.lignes_pieces)      ? ordre.lignes_pieces      : [];
  const mo          = Array.isArray(ordre?.lignes_mo_budget)   ? ordre.lignes_mo_budget   : [];
  const prestations = Array.isArray(ordre?.lignes_prestations) ? ordre.lignes_prestations : [];

  const totalPieces = pieces.reduce((s, l) => s + (l.quantite || 0) * (l.prix_unitaire || 0) * (1 - (l.remise || 0) / 100), 0);
  const totalMO     = mo.reduce((s, l) => s + (l.montant || 0), 0);
  const totalPresta = prestations.reduce((s, l) => {
    if (l.heures != null && l.taux_horaire != null) return s + (l.heures || 0) * (l.taux_horaire || 0) * (1 - (l.remise || 0) / 100);
    return s + (l.quantite || 0) * (l.prix_unitaire || 0) * (1 - (l.remise || 0) / 100);
  }, 0);
  const totalHT = totalPieces + totalMO + totalPresta;
  const hasDetail = pieces.length > 0 || mo.length > 0 || prestations.length > 0;

  const colsPieces = [
    { key: 'designation', label: 'Désignation', render: l => (
      <span>
        {l.reference && <span style={{ color: '#94A3B8', fontFamily: 'monospace', marginRight: 6, fontSize: '0.7rem' }}>{l.reference}</span>}
        {l.designation || '—'}
      </span>
    )},
    { key: 'quantite',      label: 'Qté',      align: 'right' },
    { key: 'prix_unitaire', label: 'PU HT',    align: 'right', render: l => fmtMoney(l.prix_unitaire) },
    { key: 'remise',        label: 'Remise',   align: 'right', render: l => l.remise ? `${l.remise} %` : '—' },
    { key: 'total',         label: 'Total HT', align: 'right', bold: true,
      render: l => fmtMoney((l.quantite || 0) * (l.prix_unitaire || 0) * (1 - (l.remise || 0) / 100)) },
  ];

  const colsMO = [
    { key: 'designation',  label: 'Prestation',      render: l => l.designation || "Main d'œuvre" },
    { key: 'heures',       label: 'Heures allouées', align: 'right', render: l => fmtH(l.heures) },
    { key: 'taux_horaire', label: 'Taux HT/h',       align: 'right', render: l => l.taux_horaire ? fmtMoney(l.taux_horaire) : '—' },
    { key: 'montant',      label: 'Montant HT',      align: 'right', bold: true, render: l => fmtMoney(l.montant) },
  ];

  const colsPresta = [
    { key: 'designation', label: 'Désignation', render: l => l.designation || '—' },
    { key: 'qte_heures',  label: 'Qté / Heures', align: 'right',
      render: l => l.heures != null ? fmtH(l.heures) : (l.quantite ?? '—') },
    { key: 'pu',          label: 'PU HT', align: 'right',
      render: l => l.heures != null ? (l.taux_horaire ? fmtMoney(l.taux_horaire) + '/h' : '—') : fmtMoney(l.prix_unitaire) },
    { key: 'total',       label: 'Total HT', align: 'right', bold: true, render: l => {
      const t = l.heures != null
        ? (l.heures || 0) * (l.taux_horaire || 0) * (1 - (l.remise || 0) / 100)
        : (l.quantite || 0) * (l.prix_unitaire || 0) * (1 - (l.remise || 0) / 100);
      return fmtMoney(t);
    }},
  ];

  /* ── Rendu ──────────────────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC',  }}>

      {/* Header */}
      <div style={{ background: '#1A3A6E', padding: '16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Wrench size={18} color="#fff" />
          <div>
            <p style={{ color: '#fff', fontWeight: 800, fontSize: '1rem', margin: 0 }}>Bedee</p>
            <p style={{ color: '#93C5FD', fontSize: '0.72rem', margin: '2px 0 0' }}>Signature électronique — Ordre de réparation</p>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '24px auto', padding: '0 16px 48px' }}>

        {/* Bandeau récapitulatif */}
        <div style={{ background: '#1A3A6E', borderRadius: 14, padding: '16px 20px', marginBottom: 14, color: '#fff' }}>
          <p style={{ margin: '0 0 4px', fontSize: '0.7rem', color: '#93C5FD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Ordre de réparation à valider
          </p>
          <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, letterSpacing: '-0.02em' }}>{ordre.numero}</p>
          {ordre.titre && (
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#BFDBFE' }}>{ordre.titre}</p>
          )}
        </div>

        {/* Parties */}
        <Card>
          <Section title="Parties">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Field label="Client" value={ordre.client_nom} />
              {ordre.materiel_nom && (
                <Field label="Matériel" value={`${ordre.materiel_nom}${ordre.numero_parc ? ` — N° parc ${ordre.numero_parc}` : ''}`} />
              )}
            </div>
          </Section>
        </Card>

        {/* Travaux demandés */}
        {ordre.travaux_demandes && (
          <Card>
            <Section icon={FileText} title="Travaux demandés">
              <div style={{ background: '#F8FAFC', borderRadius: 10, padding: '10px 14px', color: '#334155', fontSize: '0.85rem', lineHeight: 1.7, border: '1px solid #E2E8F0' }}>
                {ordre.travaux_demandes.split('\n').map((line, i) => {
                  const bold = line.match(/^\*\*(.+)\*\*$/);
                  return (
                    <React.Fragment key={i}>
                      {bold ? <strong style={{ color: '#1A3A6E' }}>{bold[1]}</strong> : line}
                      {'\n'}
                    </React.Fragment>
                  );
                })}
              </div>
            </Section>
          </Card>
        )}

        {/* Détail chiffré */}
        {hasDetail && (
          <Card>
            <Section title="Détail du devis">

              {pieces.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <Section icon={Package} title="Pièces et fournitures" color="#0891B2">
                    <LignesTable lignes={pieces} colonnes={colsPieces} />
                    <SousTotalLine label="Sous-total pièces HT" value={totalPieces} color="#0891B2" />
                  </Section>
                </div>
              )}

              {mo.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <Section icon={Clock} title="Main d'œuvre estimée" color="#D97706">
                    <LignesTable lignes={mo} colonnes={colsMO} />
                    <SousTotalLine label="Sous-total MO HT" value={totalMO} color="#D97706" />
                  </Section>
                </div>
              )}

              {prestations.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <Section icon={Cog} title="Prestations" color="#059669">
                    <LignesTable lignes={prestations} colonnes={colsPresta} />
                    <SousTotalLine label="Sous-total prestations HT" value={totalPresta} color="#059669" />
                  </Section>
                </div>
              )}

              <div style={{ borderTop: '2px solid #1A3A6E', paddingTop: 12, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 20 }}>
                <span style={{ color: '#475569', fontSize: '0.875rem', fontWeight: 600 }}>Total HT estimé</span>
                <span style={{ color: '#1A3A6E', fontWeight: 800, fontSize: '1.15rem' }}>{fmtMoney(totalHT)}</span>
              </div>
            </Section>
          </Card>
        )}

        {/* Zone signature */}
        <Card>
          <Section title="Votre signature">
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', color: '#475569', fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>
                Nom complet <span style={{ color: '#EF4444' }}>*</span>
              </label>
              <input
                type="text"
                value={nom}
                onChange={e => setNom(e.target.value)}
                placeholder="Votre nom et prénom"
                data-testid="signataire-nom-input"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  border: '1px solid #BFDBFE', borderRadius: 10,
                  padding: '10px 14px', fontSize: '0.875rem', color: '#1E293B',
                  outline: 'none', background: '#EFF6FF',
                }}
              />
            </div>

            <div style={{ position: 'relative', touchAction: 'none' }}>
              <canvas
                ref={canvasRef}
                width={600}
                height={180}
                data-testid="signature-canvas"
                style={{
                  display: 'block', width: '100%', cursor: 'crosshair',
                  background: '#FAFAFA', borderRadius: 12,
                  border: '2px dashed #BFDBFE',
                }}
              />
              <p style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#BFDBFE', fontSize: '0.85rem',
                pointerEvents: 'none', margin: 0, whiteSpace: 'nowrap', userSelect: 'none',
              }}>
                Signez ici
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                onClick={clearCanvas}
                data-testid="btn-effacer"
                style={{
                  padding: '11px 18px', borderRadius: 12,
                  border: '1px solid #BFDBFE', background: '#EFF6FF',
                  color: '#1A3A6E', fontSize: '0.875rem', fontWeight: 600,
                  cursor: 'pointer', flexShrink: 0,
                }}>
                Effacer
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="btn-valider"
                style={{
                  flex: 1, padding: 11, borderRadius: 12, border: 'none',
                  background: submitting ? '#BFDBFE' : '#1A3A6E',
                  color: submitting ? '#6B7280' : '#fff',
                  fontSize: '0.9rem', fontWeight: 700,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                {submitting
                  ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Envoi en cours…</>
                  : <><CheckCircle2 size={14} /> Valider l'ordre de réparation</>
                }
              </button>
            </div>

            <p style={{ color: '#94A3B8', fontSize: '0.7rem', textAlign: 'center', marginTop: 12 }}>
              En signant, vous acceptez et validez l'ordre de réparation <strong>{ordre.numero}</strong>.
            </p>
          </Section>
        </Card>

      </div>
    </div>
  );
}
